import { memo } from 'react';

import { useParams } from 'react-router-dom';

import { Box, BoxProps, Tooltip, Typography } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';

import { SimulationContractProps } from '@graphql/query/simulationContracts';

import useColorMode from '~/hooks/useColorMode';
import usePortfolio from '~/pages/Portfolio/usePortfolio';

import ContractNumber from './ContractNumber';
import useStyles from './styles';

interface ContractLabelProps extends BoxProps {
  handleExcludedsContracts: (id: string, contractNumber: string) => void;
  contract: SimulationContractProps;
  excludedContract: boolean;
}

const ContractLabel: React.ComponentType<ContractLabelProps> = ({
  handleExcludedsContracts,
  contract,
  excludedContract,
  ...props
}) => {
  const { colorTernary, theme } = useColorMode();
  const { body1, body2, factors } = useStyles();
  const { usageFactorByAngra, usageFactor } = usePortfolio();
  const { operation } = useParams();
  const sellerWidth = '130px';
  const buyerWidth = '130px';
  const modulationWidth = '110px';
  const factorColumns = usageFactorByAngra || usageFactor ? Number(Boolean(usageFactorByAngra)) + 1 : 0;
  const factorsWidth = `${factorColumns * 80}px`;

  const percentValue = (value: number | undefined | null) => {
    if (value) {
      return Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(value * 100) + '%';
    } else {
      return '';
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="flex-start"
      position="sticky"
      left="0"
      bgcolor={colorTernary(theme.palette.grey[800], 'white')}
      zIndex={2}
      {...props}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="40px"
        width="64px"
        padding="0 8px"
        borderTop={`1px solid ${colorTernary('#525252', '#26262612')}`}
        onClick={() => handleExcludedsContracts(contract.id, contract.contractNumber)}
        style={{ cursor: 'pointer' }}
      >
        {excludedContract ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" color="primary" />}
      </Box>
      <Box
        display="grid"
        gridTemplateColumns={`72px ${modulationWidth} 24px 32px ${sellerWidth} ${buyerWidth}`}
        alignItems="center"
        justifyContent="flex-end"
        gridGap="6px"
        height="40px"
        borderTop={`1px solid ${colorTernary('#525252', '#26262612')}`}
      >
        <ContractNumber contractId={contract.id} contractNumber={contract.contractNumber} />
        <Tooltip title={contract.modulation || '-'} placement="top" arrow>
          <Typography color="inherit" className={body1} style={{ width: modulationWidth }}>
            {contract.modulation || '-'}
          </Typography>
        </Tooltip>
        <Typography color="inherit" className={body1}>
          {contract.submarket}
        </Typography>
        <Typography color="inherit" className={body1}>
          {contract.energyType}
        </Typography>
        <Tooltip title={contract.sellerName} placement="top" arrow>
          <Typography color="inherit" className={body2} style={{ width: sellerWidth }}>
            {contract.sellerName}
          </Typography>
        </Tooltip>
        <Tooltip title={contract.buyerName} placement="top" arrow>
          <Typography color="inherit" className={body2} style={{ width: buyerWidth }}>
            {contract.buyerName}
          </Typography>
        </Tooltip>
      </Box>
      {operation === 'sale' && (usageFactorByAngra || usageFactor) && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          width={factorsWidth}
          flexShrink={0}
          marginLeft="auto"
          height="40px"
          borderTop={`1px solid ${colorTernary('#525252', '#26262612')}`}
        >
          {usageFactorByAngra && (
            <Typography color="inherit" className={factors}>
              {percentValue(contract.usageFactor)}
            </Typography>
          )}
          {(usageFactorByAngra || usageFactor) && (
            <Typography color="inherit" className={factors}>
              {percentValue(contract.appliedUsageFactor)}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default memo(ContractLabel);
